// src/crypto/CryptoBox.ts
import nacl from 'tweetnacl';
export class CryptoBox {
    /**
     * Generates a new Curve25519 key pair for use with nacl.box (crypto_box).
     * Returns the keys as Base64 encoded strings.
     */
    static generateKeyPair() {
        const keyPair = nacl.box.keyPair();
        // Convert the keys to Base64 strings for compatibility
        const publicKeyBase64 = CryptoBox._encodeBase64(keyPair.publicKey);
        const privateKeyBase64 = CryptoBox._encodeBase64(keyPair.secretKey);
        return { publicKey: publicKeyBase64, privateKey: privateKeyBase64 };
    }
    /**
     * Encrypts a message using the recipient's public key and sender's private key.
     * Outputs a Base64 string containing the nonce and ciphertext (nonce:ciphertext).
     *
     * @param message   The plaintext message to encrypt.
     * @param publicKey The recipient's public key (Base64 string).
     * @param privateKey The sender's private key (Base64 string).
     * @returns Promise that resolves to the encrypted message string.
     */
    static async encrypt(message, publicKey, privateKey) {
        const pub = CryptoBox._decodeBase64(publicKey);
        const priv = CryptoBox._decodeBase64(privateKey);
        if (pub.length !== nacl.box.publicKeyLength || priv.length !== nacl.box.secretKeyLength) {
            throw new Error('Invalid key length');
        }
        const msgBytes = CryptoBox._encodeUTF8(message);
        const nonce = nacl.randomBytes(nacl.box.nonceLength);
        const cipher = nacl.box(msgBytes, nonce, pub, priv);
        // Combine nonce + cipher into single blob
        const full = new Uint8Array(nonce.length + cipher.length);
        full.set(nonce, 0);
        full.set(cipher, nonce.length);
        const b64 = CryptoBox._encodeBase64(full);
        return b64;
    }
    /**
     * Decrypts an encrypted message using the sender's public key and recipient's private key.
     * The encryptedData should be a Base64 string with nonce and ciphertext (nonce:ciphertext).
     *
     * @param encryptedData The encrypted message string (nonce and ciphertext in Base64).
     * @param publicKey     The sender's public key (Base64 string).
     * @param privateKey    The recipient's private key (Base64 string).
     * @returns Promise that resolves to the decrypted plaintext message.
     */
    static async decrypt(encryptedData, publicKey, privateKey) {
        const full = CryptoBox._decodeBase64(encryptedData);
        if (full.length < nacl.box.nonceLength) {
            throw new Error('Data too short');
        }
        const nonce = full.subarray(0, nacl.box.nonceLength);
        const cipher = full.subarray(nacl.box.nonceLength);
        const pub = CryptoBox._decodeBase64(publicKey);
        const priv = CryptoBox._decodeBase64(privateKey);
        const decrypted = nacl.box.open(cipher, nonce, pub, priv);
        if (!decrypted) {
            throw new Error('Decryption failed: authentication check failed');
        }
        return CryptoBox._decodeUTF8(decrypted);
    }
    static _encodeBase64(data) {
        let binary = '';
        for (let i = 0; i < data.byteLength; i++) {
            binary += String.fromCharCode(data[i]);
        }
        return btoa(binary);
    }
    /** Decodes a Base64 string to a Uint8Array (browser-safe) */
    static _decodeBase64(base64) {
        const binary = atob(base64);
        const arr = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            arr[i] = binary.charCodeAt(i);
        }
        return arr;
    }
    /** Encodes a UTF-8 string to a Uint8Array of bytes */
    static _encodeUTF8(str) {
        return new TextEncoder().encode(str);
    }
    /** Decodes a Uint8Array of UTF-8 bytes into a string */
    static _decodeUTF8(bytes) {
        return new TextDecoder().decode(bytes);
    }
}
